import { useMemo } from 'react'

import {
  dateChanged,
  descriptionContentChanged,
} from '../utils/normalizeDescription'

function normalizeString(value: string | null | undefined): string {
  return value?.trim() ?? ''
}

function combinedValueChanged(
  currentPrimary: string | null | undefined,
  currentSecondary: string | null | undefined,
  previousPrimary: string | null | undefined,
  previousSecondary: string | null | undefined,
): boolean {
  return (
    `${normalizeString(currentPrimary)}\u241f${normalizeString(currentSecondary)}` !==
    `${normalizeString(previousPrimary)}\u241f${normalizeString(previousSecondary)}`
  )
}

// TypeScript interfaces for each opportunity type
export interface FederalContractDiffs {
  deadlineChanged: boolean
  typeChanged: boolean
  naicsChanged: boolean
  pscChanged: boolean
  setAsideChanged: boolean
  descriptionChanged: boolean
}

export interface FederalGrantDiffs {
  closeDateChanged: boolean
  categoryChanged: boolean
  descriptionChanged: boolean
}

export interface SledContractDiffs {
  dueDateChanged: boolean
  typeChanged: boolean
  descriptionChanged: boolean
}

type NoticeDiffs = FederalContractDiffs | FederalGrantDiffs | SledContractDiffs

interface UseNoticeDiffsResult<T extends NoticeDiffs> {
  /** Diff data for each notice by ID */
  diffsByNoticeId: Map<string, T>
  /** Whether the latest notice has any changes from its predecessor */
  latestHasChanges: boolean
  /** Specific changes in the latest amendment (for timeline highlighting) */
  latestDiffs: T | null
}

/**
 * Headless hook for computing diffs between federal contract notices
 * Separates diff computation logic from UI rendering
 */
export function useFederalContractDiffs(
  notices: Array<{
    id: string
    response_deadline?: string | null
    notice_type?: string | null
    naics_code?: string | null
    naics_industry?: string | null
    psc_code?: string | null
    psc_industry?: string | null
    set_aside_code?: string | null
    set_aside_description?: string | null
    description?: string | null
  }>,
): UseNoticeDiffsResult<FederalContractDiffs> {
  return useMemo(() => {
    const diffsByNoticeId = new Map<string, FederalContractDiffs>()

    // Early exit for single or empty notices
    if (notices.length <= 1) {
      return { diffsByNoticeId, latestHasChanges: false, latestDiffs: null }
    }

    // Compute diffs for each notice (except the original)
    for (let i = 0; i < notices.length - 1; i++) {
      const current = notices[i]
      const previous = notices[i + 1]

      const diffs: FederalContractDiffs = {
        deadlineChanged: dateChanged(
          current.response_deadline,
          previous.response_deadline,
        ),
        typeChanged:
          normalizeString(current.notice_type) !==
          normalizeString(previous.notice_type),
        naicsChanged: combinedValueChanged(
          current.naics_code,
          current.naics_industry,
          previous.naics_code,
          previous.naics_industry,
        ),
        pscChanged: combinedValueChanged(
          current.psc_code,
          current.psc_industry,
          previous.psc_code,
          previous.psc_industry,
        ),
        setAsideChanged: combinedValueChanged(
          current.set_aside_code,
          current.set_aside_description,
          previous.set_aside_code,
          previous.set_aside_description,
        ),
        descriptionChanged: descriptionContentChanged(
          current.description,
          previous.description,
        ),
      }

      diffsByNoticeId.set(current.id, diffs)
    }

    const latestDiffs = diffsByNoticeId.get(notices[0].id) ?? null
    const latestHasChanges = latestDiffs
      ? latestDiffs.deadlineChanged ||
        latestDiffs.typeChanged ||
        latestDiffs.naicsChanged ||
        latestDiffs.pscChanged ||
        latestDiffs.setAsideChanged ||
        latestDiffs.descriptionChanged
      : false

    return { diffsByNoticeId, latestHasChanges, latestDiffs }
  }, [notices])
}

/**
 * Headless hook for computing diffs between federal grant notices
 */
export function useFederalGrantDiffs(
  notices: Array<{
    id: string
    close_date?: string | null
    opportunity_category?: string | null
    description?: string | null
  }>,
): UseNoticeDiffsResult<FederalGrantDiffs> {
  return useMemo(() => {
    const diffsByNoticeId = new Map<string, FederalGrantDiffs>()

    if (notices.length <= 1) {
      return { diffsByNoticeId, latestHasChanges: false, latestDiffs: null }
    }

    for (let i = 0; i < notices.length - 1; i++) {
      const current = notices[i]
      const previous = notices[i + 1]

      const diffs: FederalGrantDiffs = {
        closeDateChanged: dateChanged(current.close_date, previous.close_date),
        categoryChanged:
          normalizeString(current.opportunity_category) !==
          normalizeString(previous.opportunity_category),
        descriptionChanged: descriptionContentChanged(
          current.description,
          previous.description,
        ),
      }

      diffsByNoticeId.set(current.id, diffs)
    }

    const latestDiffs = diffsByNoticeId.get(notices[0].id) ?? null
    const latestHasChanges = latestDiffs
      ? latestDiffs.closeDateChanged ||
        latestDiffs.categoryChanged ||
        latestDiffs.descriptionChanged
      : false

    return { diffsByNoticeId, latestHasChanges, latestDiffs }
  }, [notices])
}

/**
 * Headless hook for computing diffs between SLED contract notices
 */
export function useSledContractDiffs(
  notices: Array<{
    id: string
    due_date?: string | null
    type?: string | null
    description?: string | null
  }>,
): UseNoticeDiffsResult<SledContractDiffs> {
  return useMemo(() => {
    const diffsByNoticeId = new Map<string, SledContractDiffs>()

    if (notices.length <= 1) {
      return { diffsByNoticeId, latestHasChanges: false, latestDiffs: null }
    }

    for (let i = 0; i < notices.length - 1; i++) {
      const current = notices[i]
      const previous = notices[i + 1]

      const diffs: SledContractDiffs = {
        dueDateChanged: dateChanged(current.due_date, previous.due_date),
        typeChanged:
          normalizeString(current.type) !== normalizeString(previous.type),
        descriptionChanged: descriptionContentChanged(
          current.description,
          previous.description,
        ),
      }

      diffsByNoticeId.set(current.id, diffs)
    }

    const latestDiffs = diffsByNoticeId.get(notices[0].id) ?? null
    const latestHasChanges = latestDiffs
      ? latestDiffs.dueDateChanged ||
        latestDiffs.typeChanged ||
        latestDiffs.descriptionChanged
      : false

    return { diffsByNoticeId, latestHasChanges, latestDiffs }
  }, [notices])
}
